<template>
  <div>
    <AllowBlockEmailModalForm
      v-model="localValue"
      :ticket-details="config.item"
      :ticket-action="initialTicketAction"
      :show-close-ticket="true"
      :disabled="false"
      @update:valid="onValidUpdate"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { AllowBlockScope, TicketAction, TicketActionSeverity } from "@/constants/tickets";
import { getDomainFromEmail } from "@coronet/cui";
import { type TicketsScope } from "@/_store/roles.module";
import AllowBlockEmailModalForm from "./forms/AllowBlockEmailModalForm.vue";
import { isActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  components: { AllowBlockEmailModalForm },
  props: {
    config: {
      type: Object as PropType<
        DialogDataConfig<
          TicketDetails & {
            closeTicket?: boolean;
            allowAction: boolean;
            enabledActions: TicketAction[];
            name?: string;
            scope?: AllowBlockScope;
          },
          TicketAction
        >
      >,
      required: true,
    },
  },
  setup(props, { emit }) {
    const valid = ref<boolean>(false);

    const localValue = ref({
      action: "" as TicketAction,
      closeTicket: false,
      description: "",
      scope: AllowBlockScope.LOCAL,
      metadata: {
        allRecipients: true as const,
        recipients: [] as [],
      },
    });

    const showCloseTicket = computed(() => {
      return !isActionRestricted(
        RolePermissionsScope.TICKETS,
        props.config.item.moduleName as keyof TicketsScope,
        TicketActionSeverity.LOW
      );
    });

    const initialTicketAction = computed(() => {
      return props.config.item.allowAction
        ? TicketAction.APPROVE_EMAIL_WITH_OPTIONS
        : TicketAction.DISCARD_EMAIL_WITH_OPTIONS;
    });

    const onValidUpdate = (newValid: boolean) => {
      valid.value = newValid;
      emit("update:valid", newValid);
    };

    const name = computed(() => {
      if (
        [TicketAction.APPROVE_EMAILS_FROM_DOMAIN, TicketAction.DISCARD_EMAILS_FROM_DOMAIN].includes(
          localValue.value.action!
        )
      ) {
        return getDomainFromEmail(props.config.item.sections.emailMetaData?.senderEmail);
      }
      if (
        [TicketAction.APPROVE_EMAILS_FROM_SENDER, TicketAction.DISCARD_EMAILS_FROM_SENDER].includes(
          localValue.value.action!
        )
      ) {
        return props.config.item.sections.emailMetaData?.senderEmail;
      }
      return "";
    });

    watch(
      localValue,
      (newValue) => {
        emit("update:localValue", {
          ...props.config,
          item: {
            ...props.config.item,
            closeTicket: newValue.closeTicket,
            description: newValue.description,
            name: name.value, // this field is used in toast message, so it's important :)
            scope: newValue.scope,
          },
          ...newValue,
        });
      },
      { deep: true, immediate: true }
    );

    return {
      localValue,
      showCloseTicket,
      TicketAction,
      valid,
      onValidUpdate,
      initialTicketAction,
    };
  },
});
</script>
